import * as React from 'react';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { Link, matchPath } from 'react-router-dom';

import { MenuSectionItem } from 'shared/newEntities/components/MenuSection';
import { IconSize } from 'shared/entities/components/Icon';
import {
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import Typography from 'shared/newComponents/Typography';
import { ComponentType } from 'shared/entities/components/Component';
import ExternalLink from 'shared/components/ExternalLink';
import Label from 'shared/newComponents/Label';
import { useRouterStore, useUiStore } from 'stores';
import { PopupPosition } from 'shared/entities/components/Popup';

import Reference from '../Reference';

import './MenuSection.modules.scss';

type Props = MenuSectionItem & {
  className?: string;
  hidden?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  /** Разделу нужно внимание: метка на иконке, видна и в свёрнутом меню */
  withAttentionMark?: boolean;
};
const MenuSection: React.FC<Props> = ({
  Icon,
  title,
  isBeta,
  isNew,
  className,
  action,
  hidden = false,
  onClick,
  isImportant,
  disabled,
  withAttentionMark = false
}: Props) => {
  const routerStore = useRouterStore();
  const uiStore = useUiStore();

  const isActive =
    (action.type === ComponentType.link &&
      !!matchPath(routerStore.pathname, action.mask)) ||
    (action.type === ComponentType.div && action.isActive);

  const content = (
    <>
      <div styleName="menu-section__left">
        <div styleName="menu-section__icon-box">
          <Icon
            iconSize={uiStore.isDeskTopMDSize ? IconSize.XM : IconSize.L}
            styleName={cn(
              'menu-section__icon',
              isImportant && 'menu-section__icon_important'
            )}
          />
          {withAttentionMark && (
            <span
              styleName={cn(
                'menu-section__mark',
                uiStore.isDeskTopMDSize && 'menu-section__mark_small'
              )}
            />
          )}
        </div>
        {!hidden && (
          <Typography
            styleName={cn(
              'menu-section__title',
              isImportant && 'menu-section__title_important'
            )}
            fontType={TypographyType.text}
            fontSize={
              uiStore.isDeskTopMDSize ? TypographySize.s : TypographySize.m
            }
            fontWeight={TypographyWeight.semiBold}
            fontColor={null}
          >
            {title}
          </Typography>
        )}
      </div>
      {!hidden && (isNew || isBeta) && (
        <Label
          styleName="menu-section__tag"
          fontSize={
            uiStore.isDeskTopMDSize ? TypographySize.s : TypographySize.m
          }
        >
          {(t) =>
            t(`withLabel.${isNew ? 'new' : 'beta'}`, {
              ns: 'components'
            })
          }
        </Label>
      )}
    </>
  );

  let el: React.ReactNode | null = null;

  switch (action.type) {
    case ComponentType.link: {
      el = (
        <Link
          styleName={cn(
            'menu-section',
            isActive && 'menu-section_active',
            hidden && 'menu-section_hidden'
          )}
          className={cn(disabled && 'disabled', className)}
          to={action.to}
          onClick={onClick}
        >
          {content}
        </Link>
      );
      break;
    }
    case ComponentType.div: {
      el = (
        <div
          styleName={cn(
            'menu-section',
            isActive && 'menu-section_active',
            hidden && 'menu-section_hidden'
          )}
          className={cn(disabled && 'disabled', className)}
          onClick={action.onClick}
        >
          {content}
        </div>
      );
      break;
    }
    default: {
      el = (
        <ExternalLink
          styleName={cn(
            'menu-section',
            isActive && 'menu-section_active',
            hidden && 'menu-section_hidden'
          )}
          className={cn(disabled && 'disabled', className)}
          href={action.href}
        >
          {content}
        </ExternalLink>
      );
    }
  }

  return hidden ? (
    <Reference
      trigger={el}
      title={title}
      position={[PopupPosition.rightCenter]}
      styleName="menu-section__icon-wrapper"
    />
  ) : (
    el
  );
};

export default observer(MenuSection);
